<template>
    <div>
        <bubble-menu
            class="bubble-menu"
            :tippy-options="{ duration: 100 }"
            :editor="editor"
        >
            <button
                type="button"
                :class="[
                    editor.isActive('bold')
                        ? ' border-z-primary-400 bg-iron-800'
                        : 'border-iron-900 bg-iron-900',
                    'rounded-l-md border p-2',
                ]"
                @click="editor.chain().focus().toggleBold().run()"
            >
                <Bold class="text-white" :size="16" stroke-width="2.2" />
            </button>
            <button
                type="button"
                :class="[
                    editor.isActive('italic')
                        ? ' border-z-primary-400 bg-iron-800'
                        : 'border-iron-900 bg-iron-900',
                    ' border p-2',
                ]"
                @click="editor.chain().focus().toggleItalic().run()"
            >
                <Italic class="text-white" :size="16" stroke-width="2.2" />
            </button>
            <button
                type="button"
                :class="[
                    editor.isActive('underline')
                        ? ' border-z-primary-400 bg-iron-800'
                        : 'border-iron-900 bg-iron-900',
                    ' border p-2',
                ]"
                @click="editor.chain().focus().toggleUnderline().run()"
            >
                <UnderlineIcon
                    class="text-white"
                    :size="16"
                    stroke-width="2.2"
                />
            </button>
            <button
                type="button"
                :class="[
                    editor.isActive('link')
                        ? ' border-z-primary-400 bg-iron-800'
                        : 'border-iron-900 bg-iron-900',
                    'rounded-r-md border p-2',
                ]"
                @click="setLink"
            >
                <LinkIcon class="text-white" :size="16" stroke-width="2.2" />
            </button>
        </bubble-menu>

        <div class="mt-0.5">
            <EditorContent :editor="editor"></EditorContent>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Editor, BubbleMenu, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { onBeforeUnmount } from "vue";
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Link as LinkIcon,
} from "lucide-vue-next";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";

const model = defineModel<string>();

const editor = new Editor({
    content: `${model.value}`,
    extensions: [
        StarterKit,
        Underline,
        Link.configure({ openOnClick: false, defaultProtocol: "https" }),
        Image.configure({
            HTMLAttributes: {
                style: "width: 150px; height: 150px;",
            },
        }),
    ],
    onUpdate({ editor }) {
        model.value = editor.getHTML();
    },
    editorProps: {
        attributes: {
            class: "min-h-[300px] max-w-full focus:outline-none rounded-lg border border-iron-300 focus:border-z-primary-300 px-3 py-2 text-sm text-iron-900 shadow-xs ring-purple-100 focus:outline-none focus:ring-2",
        },
    },
});

function setLink() {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
        return;
    }

    // empty
    if (url === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();

        return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}

function addImage() {
    const url = window.prompt("URL");

    if (url) {
        editor.chain().focus().setImage({ src: url }).run();
    }
}

onBeforeUnmount(() => {
    editor.destroy();
});
</script>

<style>
.tiptap a {
    color: #3182ce;
    text-decoration: underline;
}
</style>
